import {
  CibliError,
  CVSensitiveInfo,
  DEFAULT_LANGUAGE,
  DEFAULT_TEMPLATE_ID,
  FullCV,
  INTERVIEW_PAYMENT_AMOUNT_CENTS,
  Prisma,
  TemplateId,
  transformGeneratedCVToFullCV,
  AnalyticsEvent,
} from '@cibli/shared';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import CVStatusBar from '@/features/cv/components/CVStatusBar';
import { getCvExemple } from '@/features/cv/CV_exemple';
import CVDisplayScreen from '@/features/cv/pages/CVDisplayScreen';
import CVGenerationScreen from '@/features/cv/pages/CVGenerationScreen';
import CVReceptionScreen from '@/features/cv/pages/CVReceptionScreen';
import CVTranslatingScreen from '@/features/cv/pages/CVTranslatingScreen';
import { useMatchingJobs } from '@/features/matching/hooks/useMatchingJobs';
import MatchingJobsScreen from '@/features/matching/pages/MatchingJobsScreen';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { useTrackEvent } from '@/lib/analytics/use-track-event';
import { generateCV, translateCV } from '@/services/interview';

import { INTERVIEW_STEPS, useInterviewFlowContext } from '../contexts/interview-flow.context';

interface Props {
  defaultCV?: Partial<FullCV> | null;
  onCancel: (step: INTERVIEW_STEPS) => void;
}

export default function InterviewSummaryScreen({ defaultCV, onCancel }: Props) {
  const { interview } = useInterviewContext();
  const { user } = useUserContext();
  const {
    cvTemplateId,
    cvTemplateParams,
    setCvTemplateId,
    setCvTemplateParams,
    setCurrentLocaleCv,
  } = useCVTemplateContext();
  const { i18n, t } = useTranslation();
  const { step, generatedCV, setSummarySubStep } = useInterviewFlowContext();
  const trackEvent = useTrackEvent();
  const hasTrackedEndedInterview = useRef(false);
  const hasAttemptedServerCV = useRef(false);
  const [cv, setCv] = useState<FullCV | null>(null);
  const [isGeneratingCV, setIsGeneratingCV] = useState(false);
  const [isTranslatingCV, setIsTranslatingCV] = useState(false);
  const [isEditingTranslatedCV, setIsEditingTranslatedCV] = useState(false);
  const [hasTranslatedCV, setHasTranslatedCV] = useState(false);
  const [hasCV, setHasCV] = useState(false);
  const [cvId, setCvId] = useState<string | null>(null);
  const [receptionStepDone, setReceptionStepDone] = useState(false);
  const [receptionPhase, setReceptionPhase] = useState<'appearance' | 'reception'>('appearance');

  // Preload carousel logos as early as possible (during CV reception)
  useEffect(() => {
    const LOGO_URLS = [
      '/cibli-logo.svg',
      'https://logo.clearbit.com/leclerc.com',
      'https://logo.clearbit.com/ratp.fr',
      'https://logo.clearbit.com/securitas.com',
      'https://logo.clearbit.com/amazon.fr',
      'https://logo.clearbit.com/elior-group.com',
      'https://logo.clearbit.com/decathlon.com',
      'https://logo.clearbit.com/accor.com',
      'https://logo.clearbit.com/orange.com',
      'https://logo.clearbit.com/aphp.fr',
      'https://logo.clearbit.com/chru-lille.fr',
    ];
    for (const url of LOGO_URLS) {
      const img = new Image();
      img.src = url;
    }
  }, []);

  // Preload matching jobs as soon as cvId is available (during CV reception)
  const preloadedMatchingJobs = useMatchingJobs(cvId || '');

  useEffect(() => {
    if (!hasTrackedEndedInterview.current) {
      hasTrackedEndedInterview.current = true;
      trackEvent({ event: AnalyticsEvent.ENDED_INTERVIEW });
    }
  }, [trackEvent]);

  const handleReceptionDone = useCallback(() => {
    setReceptionStepDone(true);
  }, []);

  const handleBackToEditor = useCallback(() => {
    setCvId(null);
    setReceptionPhase('appearance');
  }, []);

  const handleBackToReception = useCallback(() => {
    setReceptionStepDone(false);
  }, []);

  const sensitiveInfo: CVSensitiveInfo | null = useMemo(() => {
    if (!user) return null;

    return {
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phoneNumber,
      email: user.email || null,
    };
  }, [user]);

  const handleValidateReception = useCallback(
    async (templateId: TemplateId, templateParams: Record<string, unknown>) => {
      if (!interview || !cv || !sensitiveInfo) {
        throw new Error('CV data is not ready');
      }

      const createdCV = await generateCV(interview.id, {
        ...sensitiveInfo,
        ...cv,
        interviewId: interview.id,
        template: templateId,
        templateParams: templateParams as Prisma.JsonValue,
      });
      if (!createdCV.id) {
        throw new Error('CV not created');
      }

      setCvTemplateId(templateId);
      setCvTemplateParams(templateParams);
      setCvId(createdCV.id);
    },
    [cv, interview, sensitiveInfo, setCvTemplateId, setCvTemplateParams],
  );

  const handleGenerateCV = async () => {
    if (!interview || !cv) return;

    try {
      setIsGeneratingCV(true);
      const createdCV = await generateCV(interview.id, {
        ...sensitiveInfo,
        ...cv,
        interviewId: interview.id,
        template: cvTemplateId || DEFAULT_TEMPLATE_ID,
        templateParams: cvTemplateParams as Prisma.JsonValue,
      });
      if (!createdCV.id) {
        throw new Error('CV not created');
      }

      if (interview.paymentIntentId) {
        trackEvent({
          event: AnalyticsEvent.PAYMENT_CAPTURED,
          props: { amountCents: INTERVIEW_PAYMENT_AMOUNT_CENTS },
        });
      }

      setCvId(createdCV.id);
      setHasCV(true);
    } catch (error) {
      console.error(error);
      const message =
        error instanceof CibliError ? error.message : 'Erreur lors de la génération du CV';
      toast.error(message);
    } finally {
      setIsGeneratingCV(false);
    }
  };

  const handleGenerateTranslatedCV = async () => {
    if (!isEditingTranslatedCV || isGeneratingCV || !cv || !interview) return;
    try {
      setIsGeneratingCV(true);
      const createdCV = await generateCV(interview.id, {
        ...sensitiveInfo,
        ...cv,
        interviewId: interview.id,
        template: cvTemplateId || DEFAULT_TEMPLATE_ID,
        templateParams: cvTemplateParams as Prisma.JsonValue,
      });
      if (!createdCV.id) {
        throw new Error('CV not created');
      }

      if (interview.paymentIntentId) {
        trackEvent({
          event: AnalyticsEvent.PAYMENT_CAPTURED,
          props: { amountCents: INTERVIEW_PAYMENT_AMOUNT_CENTS },
        });
      }

      setIsEditingTranslatedCV(false);
    } catch (error) {
      console.error(error);
      const message =
        error instanceof CibliError ? error.message : 'Erreur lors de la génération du CV traduit';
      toast.error(message);
    } finally {
      setIsGeneratingCV(false);
    }
  };

  const { booth, webRegionConfig } = useBooth();
  // Target translation locale: booth default locale (kiosk) or web region default locale (web), fallback to DEFAULT_LANGUAGE
  const baseTargetLocale = useMemo(() => {
    return (
      booth?.defaultLocaleId ||
      webRegionConfig?.defaultLocale?.codeLong ||
      DEFAULT_LANGUAGE
    );
  }, [booth?.defaultLocaleId, webRegionConfig?.defaultLocale?.codeLong]);

  const handleTranslateCV = async () => {
    if (isTranslatingCV || !cvId) return;
    try {
      setIsTranslatingCV(true);
      const { fullCvTranslated } = await translateCV(cvId, {
        targetLanguage: baseTargetLocale,
      });
      if (fullCvTranslated) {
        setCv(fullCvTranslated);
      }
      setHasTranslatedCV(true);
      setIsEditingTranslatedCV(true);
      setCurrentLocaleCv(baseTargetLocale);
    } catch (error) {
      console.error(error);
      const message =
        error instanceof CibliError ? error.message : 'Erreur lors de la traduction du CV';
      toast.error(message);
    } finally {
      setIsTranslatingCV(false);
    }
  };

  const handleCancel = () => {
    onCancel(step);
  };

  useEffect(() => {
    if (isTranslatingCV) return;
    if (hasTranslatedCV) return;
    if (!cvId) return;
    // Skip translation if candidate's interview language matches the base target locale
    if (i18n.language === baseTargetLocale) {
      setHasTranslatedCV(true);
      setIsTranslatingCV(false);
    } else {
      handleTranslateCV();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cv, hasTranslatedCV, isTranslatingCV, cvId, i18n.language, baseTargetLocale]);

  useEffect(() => {
    if (cv) return;
    if (generatedCV && sensitiveInfo) {
      setCv(
        transformGeneratedCVToFullCV(
          generatedCV,
          sensitiveInfo,
          {
            name: i18n.language,
            codeShort: i18n.language,
            codeLong: i18n.language,
          },
          defaultCV?.pictureBase64 || null,
        ),
      );
      return;
    }

    // Developer bypass: if enabled and we have user info but no interview CV, bootstrap a minimal CV
    if (import.meta.env.VITE_DEV_BYPASS_INTERVIEW === 'true' && !generatedCV && sensitiveInfo) {
      const mockCv = transformGeneratedCVToFullCV(
        getCvExemple(i18n.language),
        sensitiveInfo,
        {
          name: i18n.language,
          codeShort: i18n.language,
          codeLong: i18n.language,
        },
        defaultCV?.pictureBase64 || null,
      );

      setCv(prev => prev || mockCv);
      setHasCV(true);

      if (interview && !cvId && !hasAttemptedServerCV.current) {
        hasAttemptedServerCV.current = true;
        const cvPayload = {
          ...mockCv,
          interviewId: interview.id,
          template: cvTemplateId || DEFAULT_TEMPLATE_ID,
          templateParams: cvTemplateParams as Prisma.JsonValue,
        };
        (async () => {
          try {
            await generateCV(interview.id, cvPayload);
            if (interview.paymentIntentId) {
              trackEvent({
                event: AnalyticsEvent.PAYMENT_CAPTURED,
                props: { amountCents: INTERVIEW_PAYMENT_AMOUNT_CENTS },
              });
            }
          } catch (error) {
            console.error('DEV BYPASS: initial server CV creation failed', error);
          }
        })();
      }
    }
  }, [
    generatedCV,
    sensitiveInfo,
    defaultCV,
    interview,
    cvId,
    cv,
    i18n.language,
    cvTemplateId,
    cvTemplateParams,
    trackEvent,
  ]);

  // Report current sub-screen label to the flow context for feedback tracking
  useEffect(() => {
    if (step !== INTERVIEW_STEPS.SUMMARY || !setSummarySubStep) return;

    if (!hasCV) {
      setSummarySubStep('Génération du CV');
    } else if (cv && !cvId) {
      setSummarySubStep('Aperçu du CV');
    } else if (isTranslatingCV) {
      setSummarySubStep('Traduction du CV');
    } else if (isEditingTranslatedCV && cv) {
      setSummarySubStep('Aperçu du CV traduit');
    } else if (cvId && !receptionStepDone) {
      setSummarySubStep('Choix du modèle CV');
    } else if (cvId) {
      setSummarySubStep("Offres d'emploi");
    }

    return () => setSummarySubStep(undefined);
  }, [
    step,
    setSummarySubStep,
    hasCV,
    cv,
    cvId,
    isTranslatingCV,
    isEditingTranslatedCV,
    receptionStepDone,
  ]);

  if (!hasCV) {
    return <CVGenerationScreen onGenerated={() => setHasCV(true)} />;
  }

  // Compute which step the user is on for the status bar
  const cvSteps = [
    { label: t('CV.steps.edit') },
    { label: t('CV.steps.customize') },
    { label: t('CV.steps.receive') },
  ];
  let currentStep = 1;
  if (cv && !cvId)
    currentStep = 1; // editing content
  else if (isTranslatingCV || (isEditingTranslatedCV && cv))
    currentStep = 1; // still editing
  else if (cvId && receptionPhase === 'reception')
    currentStep = 3; // reception
  else if (cvId) currentStep = 2; // choosing appearance

  const statusBar = <CVStatusBar currentStep={currentStep} steps={cvSteps} />;

  if (cv && !cvId) {
    return (
      <CVDisplayScreen
        cv={cv}
        onEdit={setCv}
        onGenerateCV={handleGenerateCV}
        isGeneratingCV={isGeneratingCV}
        formConfirmationText={t('common.next')}
        statusBar={statusBar}
      />
    );
  }

  if (isTranslatingCV) {
    return <CVTranslatingScreen />;
  }

  if (isEditingTranslatedCV && cv) {
    return (
      <CVDisplayScreen
        cv={cv}
        onEdit={setCv}
        onGenerateCV={handleGenerateTranslatedCV}
        isGeneratingCV={isGeneratingCV}
        formConfirmationText={t('common.next')}
        statusBar={statusBar}
      />
    );
  }

  if (cvId && !receptionStepDone) {
    return (
      <CVReceptionScreen
        cvId={cvId}
        onCancel={handleCancel}
        onContinue={handleReceptionDone}
        onBackToEditor={handleBackToEditor}
        onValidate={handleValidateReception}
        userEmail={user?.email || null}
        userPhone={user?.phoneNumber || null}
        statusBar={statusBar}
        onPhaseChange={setReceptionPhase}
      />
    );
  }

  if (cvId) {
    return (
      <MatchingJobsScreen
        cvId={cvId}
        onCancel={handleCancel}
        onBack={handleBackToReception}
        matchingJobsState={{
          jobs: preloadedMatchingJobs.jobs,
          total: preloadedMatchingJobs.total,
          hasMore: preloadedMatchingJobs.hasMore,
          loadingMore: preloadedMatchingJobs.loadingMore,
          loading: preloadedMatchingJobs.loading,
          streamedJobs: preloadedMatchingJobs.streamedJobs,
          hasSourceError: preloadedMatchingJobs.hasSourceError,
          onLoadMore: preloadedMatchingJobs.loadMore,
        }}
      />
    );
  }

  return null;
}
